import { flexRender, SortingState } from "@tanstack/react-table";
import { FC } from "react";
import { cx } from "shared/utils";
import { ArrowUp } from "untitledui-js-base";

type Props = {
  headerGroup: any;
  hasSubheader?: boolean;
  thClassName?: string;
  sortableKeys?: string[];
  //hack need to refactor
  sorted?: { key: string; desc: boolean } | SortingState;
  setSorted?: ((prev: { key: string; desc: boolean }) => void) | any;
  fixedHeader?: boolean;
};

export const TableHeader: FC<Props> = ({
  headerGroup,
  hasSubheader,
  thClassName,
  sortableKeys,
  sorted,
  setSorted,
  fixedHeader
}) => {
  return (
    <thead
      className={
        fixedHeader ? "sticky top-0 z-10 bg-white border-alpha-100 " : ""
      }
    >
      {!hasSubheader ? (
        <tr>
          {headerGroup[0].headers.map((header, idx) => (
            <th
              key={`headerGroup_${headerGroup[0].id}_header_${header.id}_${idx}`}
              style={{
                borderBottom: "1px solid rgba(28, 29, 34, 0.04)",
                minWidth:
                  header.getSize() && !isNaN(header.getSize())
                    ? header.getSize()
                    : undefined
              }}
              className={cx(
                "h-12 max-h-12 min-h-12 px-1 xl:px-4 py-3",
                thClassName,
                header.column.columnDef.meta?.headerClassName
              )}
            >
              <HeaderContent
                header={header}
                sortableKeys={sortableKeys}
                setSorted={setSorted}
                sorted={sorted}
              />
            </th>
          ))}
        </tr>
      ) : (
        headerGroup.map((group, groupIndex) => (
          <tr key={`headerGroup_${group.id}_${groupIndex}`}>
            {group.headers.map((header, idx) => (
              <th
                key={`headerGroup_${group.id}_header_${header.id}_${idx}`}
                style={{
                  borderBottom: "1px solid rgba(28, 29, 34, 0.04)",
                  minWidth:
                    header.getSize() && !isNaN(header.getSize())
                      ? header.getSize()
                      : undefined
                }}
                className={cx(
                  "h-12 max-h-12 min-h-12 px-4 py-3",
                  thClassName,
                  header.column.columnDef.meta?.headerClassName
                )}
              >
                <HeaderContent
                  header={header}
                  sortableKeys={sortableKeys}
                  setSorted={setSorted}
                  sorted={sorted}
                />
              </th>
            ))}
          </tr>
        ))
      )}
    </thead>
  );
};

const HeaderContent = ({
  header,
  sortableKeys,
  setSorted,
  sorted
}: {
  header: any;
  sortableKeys?: string[];
  setSorted?: ((prev: { key: string; desc: boolean }) => void) | any;
  sorted?: { key: string; desc: boolean } | SortingState;
}) => {
  const rawColumnId = String(header.column.columnDef.id ?? header.id ?? "");
  const columnKey = rawColumnId.split(" ")[0];
  const normalizedSortableId = sortableKeys?.find((key) => key === columnKey);
  const targetId = normalizedSortableId ?? columnKey;
  const isSortable = Boolean(normalizedSortableId);

  const isActiveSort = Array.isArray(sorted)
    ? sorted.some((el) => el.id === targetId)
    : sorted?.key === targetId;

  let currentSort:
    | SortingState[number]
    | { key: string; desc: boolean }
    | undefined;

  if (Array.isArray(sorted))
    currentSort = sorted.find((el) => el.id === targetId);
  else if (sorted?.key === targetId) currentSort = sorted;
  else currentSort = undefined;

  const currentDesc =
    currentSort && "desc" in currentSort ? currentSort.desc : undefined;

  const handleSort = () => {
    if (!isSortable || !setSorted) return;

    if (Array.isArray(sorted))
      setSorted((prev: SortingState) => {
        const existing = prev.find((el) => el.id === targetId);
        const nextDesc = existing ? !existing.desc : true;
        return [{ id: targetId, desc: nextDesc }];
      });
    else setSorted([{ id: targetId, desc: true }]);
  };

  return (
    <div
      onClick={() => {
        if (isSortable) handleSort();
      }}
      className={cx(
        "group flex items-center gap-2",
        isSortable ? "cursor-pointer" : "cursor-default",
        header.id.includes("right-align") ? "justify-end" : "justify-start"
      )}
    >
      <p
        className={cx(
          "m-0 text-xs font-medium",
          isActiveSort
            ? "text-content-900"
            : "text-content-300 group-hover:text-content-500"
        )}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </p>
      {isSortable && (
        <span
          className={cx(
            "ml-1 flex h-6 w-6 items-center justify-center rounded-full transition-all duration-200",
            isActiveSort
              ? "opacity-100 bg-alpha-80 text-content-900"
              : "opacity-0 group-hover:opacity-100 bg-transparent text-content-300 group-hover:bg-alpha-20 group-hover:text-content-500"
          )}
        >
          <ArrowUp
            size="14"
            className={cx(
              "transition-rotate shrink-0",
              currentDesc === false ? "rotate-0" : "rotate-180"
            )}
          />
        </span>
      )}
    </div>
  );
};
